// Utility functions for Docospot
// Helper functions for common tasks like date formatting, user management, etc.

import { auth, inactivityManager } from '../core/auth.js';
import { showError, showSuccess } from '../core/ui.js';

/**
 * Get current user from localStorage
 * @returns {object|null} Current user object or null if not logged in
 */
export function getCurrentUser() {
    const userStr = localStorage.getItem('current_user');
    const user = userStr ? JSON.parse(userStr) : null;
    
    // Initialize permissions if user exists and permissions not yet initialized
    if (user && window.permissions) {
        if (!window.permissions.checker || window.permissions.checker.userRole !== user.role) {
            window.permissions.init(user);
        }
    }
    
    return user;
}

/**
 * Get the effective account ID for the current user
 * For SuperAdmin: returns the selected account from localStorage
 * For regular users: returns their account_id from the user object
 * @returns {string|null} Account ID or null if not available
 */
export function getEffectiveAccountId() {
    const user = getCurrentUser();
    if (!user) return null;
    
    // SuperAdmin uses selected account (acts as if they belong to that account)
    if (user.role === 'SuperAdmin') {
        const selectedAccount = localStorage.getItem('superadmin_selected_account');
        return selectedAccount || null;
    }
    
    // Regular users use their assigned account_id
    return user.account_id || null;
}

/**
 * Set the selected account for SuperAdmin
 * @param {string} accountId - Account ID to select
 */
export async function setSuperAdminAccount(accountId) {
    const user = getCurrentUser();
    if (user && user.role === 'SuperAdmin') {
        if (accountId) {
            try {
                // Call backend to get new token with account context
                const response = await fetch('/api/auth/set-context-account?account_id=' + accountId, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                        'Content-Type': 'application/json'
                    }
                });
                
                if (response.ok) {
                    const data = await response.json();
                    // Update token and user data
                    localStorage.setItem('access_token', data.access_token);
                    localStorage.setItem('current_user', JSON.stringify(data.user));
                    localStorage.setItem('superadmin_selected_account', accountId);
                    // Reload to apply new context
                    window.location.reload();
                } else {
                    console.error('Failed to set context account');
                }
            } catch (error) {
                console.error('Error setting context account:', error);
            }
        } else {
            try {
                // Call backend to clear account context
                const response = await fetch('/api/auth/clear-context-account', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                        'Content-Type': 'application/json'
                    }
                });
                
                if (response.ok) {
                    const data = await response.json();
                    // Update token and user data
                    localStorage.setItem('access_token', data.access_token);
                    localStorage.setItem('current_user', JSON.stringify(data.user));
                    localStorage.removeItem('superadmin_selected_account');
                    // Reload to apply new context
                    window.location.reload();
                } else {
                    console.error('Failed to clear context account');
                }
            } catch (error) {
                console.error('Error clearing context account:', error);
            }
        }
    }
}

/**
 * Get the selected account ID for SuperAdmin
 * @returns {string|null} Selected account ID or null
 */
export function getSuperAdminAccount() {
    const user = getCurrentUser();
    if (user && user.role === 'SuperAdmin') {
        return localStorage.getItem('superadmin_selected_account');
    }
    return null;
}

/**
 * Format date/time string to DD-MM-YYYY HH:MM format
 * Automatically converts UTC to local timezone
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date string or '-' if invalid
 */
export function formatDateTime(dateString) {
    if (!dateString) return '-';
    const date = new Date(dateString);
    
    // Use toLocaleString to automatically convert UTC to local timezone
    // Format: DD-MM-YYYY HH:MM (24-hour time, browser's local timezone)
    return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    }).replace(',', '');
}

/**
 * Logout current user
 * Clears auth token, user data, and stops inactivity timer
 */
export function logout() {
    console.log('🚪 Logout initiated');
    
    // Stop inactivity timer
    inactivityManager.stop();
    
    // Clear authentication data
    auth.removeToken();
    localStorage.removeItem('current_user');
    
    console.log('🚪 Auth data cleared, updating navigation');
    
    // Update navigation (will call showLoginScreen when not authenticated)
    if (window.updateNavigation) {
        window.updateNavigation();
    } else {
        console.error('updateNavigation not available on window');
    }
    
    // Also explicitly show login screen as backup
    if (window.showLoginScreen) {
        console.log('🚪 Explicitly calling showLoginScreen');
        window.showLoginScreen();
    }
    
    showSuccess('Logged out successfully');
    
    console.log('🚪 Logout complete');
}

/**
 * Auto-logout user (session expired or inactivity)
 * @param {string} message - Message to display to user
 */
export function autoLogout(message = 'Your session has expired. Please log in again.') {
    console.log('🚪 Auto-logout initiated:', message);
    
    // Clear authentication data FIRST before any other operations
    auth.removeToken();
    localStorage.removeItem('current_user');
    
    // Stop inactivity timer
    inactivityManager.stop();
    
    // Update UI to show logged out state
    if (window.updateNavigation) {
        window.updateNavigation();
    }
    
    // Show login form
    if (window.showLoginScreen) {
        window.showLoginScreen();
    }
    
    // Show the message to user
    showError(message);
    
    // Optionally scroll to top to ensure login form is visible
    window.scrollTo({ top: 0, behavior: 'smooth' });
}

/**
 * Format duration in milliseconds to human-readable format
 * @param {number} ms - Duration in milliseconds
 * @returns {string} Formatted duration (e.g., "2h 30m")
 */
export function formatDuration(ms) {
    if (!ms || ms < 0) return '-';
    
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) {
        return `${days}d ${hours % 24}h`;
    } else if (hours > 0) {
        return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
        return `${minutes}m`;
    } else {
        return `${seconds}s`;
    }
}

/**
 * Format date to DD-MM-YYYY (no time)
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date string or '-' if invalid
 */
export function formatDate(dateString) {
    if (!dateString) return '-';
    const date = new Date(dateString);
    
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
}

/**
 * Format time to HH:MM (no date)
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted time string or '-' if invalid
 */
export function formatTime(dateString) {
    if (!dateString) return '-';
    const date = new Date(dateString);
    
    return date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    });
}

/**
 * Debounce function - delays function execution until after wait time
 * @param {function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {function} Debounced function
 */
export function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

/**
 * Throttle function - ensures function is called at most once per interval
 * @param {function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {function} Throttled function
 */
export function throttle(func, limit) {
    let inThrottle;
    return function(...args) {
        if (!inThrottle) {
            func.apply(this, args);
            inThrottle = true;
            setTimeout(() => inThrottle = false, limit);
        }
    };
}

/**
 * Safely parse JSON with fallback
 * @param {string} jsonString - JSON string to parse
 * @param {any} fallback - Fallback value if parsing fails
 * @returns {any} Parsed object or fallback
 */
export function safeJsonParse(jsonString, fallback = null) {
    try {
        return JSON.parse(jsonString);
    } catch (error) {
        console.warn('Failed to parse JSON:', error);
        return fallback;
    }
}

/**
 * Copy text to clipboard
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} True if successful
 */
export async function copyToClipboard(text) {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch (error) {
        console.error('Failed to copy to clipboard:', error);
        return false;
    }
}

/**
 * Generate a random ID
 * @param {number} length - Length of ID
 * @returns {string} Random ID
 */
export function generateId(length = 10) {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
}

/**
 * Check if value is empty (null, undefined, empty string, empty array, empty object)
 * @param {any} value - Value to check
 * @returns {boolean} True if empty
 */
export function isEmpty(value) {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim() === '';
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return Object.keys(value).length === 0;
    return false;
}

/**
 * Handle API errors consistently
 * @param {Error} error - Error object
 * @param {string} context - Context description (e.g., "Loading users")
 */
export function handleApiError(error, context = 'Operation') {
    console.error(`❌ ${context} Error:`, error);
    
    // Don't show error message if it's an auth error (user was auto-logged out)
    if (!error.isAuthError) {
        showError(error.message || `${context} failed`);
    }
}

export default {
    getCurrentUser,
    getEffectiveAccountId,
    setSuperAdminAccount,
    getSuperAdminAccount,
    formatDateTime,
    formatDate,
    formatTime,
    formatDuration,
    logout,
    autoLogout,
    debounce,
    throttle,
    safeJsonParse,
    copyToClipboard,
    generateId,
    isEmpty,
    handleApiError
};
